import type { Motif } from '@snapchat/snap-design-system-marketing';
import {
  defaultMotif,
  FontFamily,
  m,
  mergeMotifs,
  MotifComponent,
  MotifScheme,
} from '@snapchat/snap-design-system-marketing';

/** Motif for the Snap.com homepage */
export const snapMotif: Motif = mergeMotifs(defaultMotif, {
  name: 'Snap',
  fontFamily: [FontFamily.GHOST_SANS, FontFamily.PROGRAM_OT, FontFamily.GRAPHIK],
  [MotifScheme.DEFAULT]: {
    name: 'Snap Yellow background',
    [MotifComponent.BUTTON]: {
      '--button-border-width': '2px',
      '--button-hover-shadow': m('--box-shadow-l'),
      '--button-secondary-bg-color': m('--primary-v100'),
      '--button-secondary-fg-color': m('--neutral-v700'),
      '--button-secondary-border-color': m('--neutral-v500'),
      '--button-secondary-hover-bg-color': m('--primary-v50'),
      '--button-secondary-hover-border-color': m('--neutral-v700'),
    },
    [MotifComponent.CAROUSEL]: {
      '--carousel-active-dot-color': m('--neutral-v700'),
      '--carousel-card-bg-color': m('--primary-v100'),
      '--carousel-card-hover-bg-color': m('--primary-v50'),
      '--carousel-card-border-width': '2px',
      '--carousel-card-border-color': m('--neutral-v500'),
      '--carousel-card-hover-border-color': m('--neutral-v700'),
      '--carousel-card-desktop-grid-gap': m('--spacing-xl'),
      '--carousel-card-box-shadow': 'none',
      '--carousel-card-hover-box-shadow': m('--box-shadow-m'),
    },
    [MotifComponent.SUMMARY_CARD]: {
      '--summary-card-bg-color': m('--primary-v100'),
      '--summary-card-hover-bg-color': m('--primary-v50'),
      '--summary-card-border-width': '2px', // Ensure 'px' unit is added here - used in calc() which requires units
      '--summary-card-border-color': m('--neutral-v500'),
      '--summary-card-hover-border-color': m('--neutral-v700'),
      '--summary-card-box-shadow': 'none',
      '--summary-card-hover-box-shadow': m('--box-shadow-m'),
    },
    [MotifComponent.DROPDOWN_MENU]: {
      '--dropdown-button-border-radius': m('--border-radius-l'),
      '--dropdown-button-border-width': '2px',
      '--dropdown-button-border-color': m('--neutral-v500'),
      '--dropdown-button-hover-border-color': m('--neutral-v700'),
    },
  },
  [MotifScheme.SECONDARY]: {
    name: 'Snap Black background',
    [MotifComponent.BUTTON]: {
      '--button-border-width': '2px',
    },
    [MotifComponent.CAROUSEL]: {
      '--carousel-active-dot-color': m('--neutral-v0'),
      '--carousel-inactive-dot-color': 'rgba(133, 141, 148, 0.5)',
      '--carousel-card-bg-color': m('--neutral-v700'),
      '--carousel-card-hover-bg-color': m('--neutral-v625'),
      '--carousel-card-fg-color': m('--neutral-v0'),
      '--carousel-card-border-width': '2px',
      '--carousel-card-border-color': m('--neutral-v600'),
      '--carousel-card-hover-border-color': m('--neutral-v500'),
      '--carousel-card-desktop-grid-gap': m('--spacing-xl'),
      '--carousel-card-box-shadow': 'none',
      '--carousel-card-hover-box-shadow': m('--box-shadow-m'),
    },
    [MotifComponent.SUMMARY_CARD]: {
      '--summary-card-bg-color': m('--neutral-v700'),
      '--summary-card-hover-bg-color': m('--neutral-v625'),
      '--summary-card-fg-color': m('--neutral-v0'),
      '--summary-card-title-color': m('--neutral-v0'),
      '--summary-card-description-color': m('--neutral-v0'),
      '--summary-card-border-width': '2px', // Ensure 'px' unit is added here - used in calc() which requires units
      '--summary-card-border-color': m('--neutral-v600'),
      '--summary-card-hover-border-color': m('--neutral-v500'),
      '--summary-card-box-shadow': 'none',
      '--summary-card-hover-box-shadow': m('--box-shadow-m'),
    },
  },
  [MotifScheme.TERTIARY]: {
    name: 'Snap White background',
    [MotifComponent.BUTTON]: {
      '--button-border-width': '2px',
      '--button-primary-border-color': m('--neutral-v500'),
      '--button-primary-hover-border-color': m('--neutral-v700'),
      '--button-primary-bg-color': m('--neutral-v0'),
      '--button-primary-hover-bg-color': m('--neutral-v700'),
      '--button-primary-hover-fg-color': m('--neutral-v0'),
    },
    [MotifComponent.CAROUSEL]: {
      '--carousel-active-dot-color': m('--neutral-v700'),
      '--carousel-inactive-dot-color': 'rgba(133, 141, 148, 0.5)',
      '--carousel-card-bg-color': m('--neutral-v0'),
      '--carousel-card-hover-bg-color': m('--neutral-v100'),
      '--carousel-card-fg-color': m('--neutral-v700'),
      '--carousel-card-border-width': '2px',
      '--carousel-card-border-color': m('--neutral-v500'),
      '--carousel-card-hover-border-color': m('--neutral-v700'),
      '--carousel-card-desktop-grid-gap': m('--spacing-xl'),
      '--carousel-card-box-shadow': 'none',
      '--carousel-card-hover-box-shadow': m('--box-shadow-m'),
    },
    [MotifComponent.SUMMARY_CARD]: {
      '--summary-card-bg-color': m('--neutral-v0'),
      '--summary-card-hover-bg-color': m('--neutral-v100'),
      '--summary-card-fg-color': m('--neutral-v700'),
      '--summary-card-title-color': m('--neutral-v700'),
      '--summary-card-description-color': m('--neutral-v700'),
      '--summary-card-border-width': '2px', // Ensure 'px' unit is added here - used in calc() which requires units
      '--summary-card-border-color': m('--neutral-v500'),
      '--summary-card-hover-border-color': m('--neutral-v700'),
      '--summary-card-box-shadow': 'none',
      '--summary-card-hover-box-shadow': m('--box-shadow-m'),
    },
  },
  [MotifScheme.QUATERNARY]: {
    name: 'Snap Gray background',
    [MotifComponent.BUTTON]: {
      '--button-border-width': '2px',
      '--button-primary-border-color': m('--neutral-v500'),
      '--button-primary-hover-border-color': m('--neutral-v700'),
      '--button-secondary-bg-color': m('--neutral-v700'),
      '--button-secondary-hover-bg-color': m('--neutral-v700'),
      '--button-secondary-border-color': m('--neutral-v700'),
      '--button-secondary-hover-border-color': m('--neutral-v700'),
      '--button-secondary-fg-color': m('--neutral-v0'),
    },
    [MotifComponent.CAROUSEL]: {
      '--carousel-active-dot-color': m('--neutral-v700'),
      '--carousel-inactive-dot-color': 'rgba(133, 141, 148, 0.5)',
      '--carousel-card-bg-color': m('--neutral-v150'),
      '--carousel-card-hover-bg-color': m('--neutral-v200'),
      '--carousel-card-fg-color': m('--neutral-v700'),
      '--carousel-card-border-width': '2px',
      '--carousel-card-border-color': m('--neutral-v500'),
      '--carousel-card-hover-border-color': m('--neutral-v700'),
      '--carousel-card-desktop-grid-gap': m('--spacing-xl'),
      '--carousel-card-box-shadow': 'none',
      '--carousel-card-hover-box-shadow': m('--box-shadow-m'),
    },
    [MotifComponent.SUMMARY_CARD]: {
      '--summary-card-bg-color': m('--neutral-v150'),
      '--summary-card-hover-bg-color': m('--neutral-v200'),
      '--summary-card-fg-color': m('--neutral-v700'),
      '--summary-card-title-color': m('--neutral-v700'),
      '--summary-card-description-color': m('--neutral-v700'),
      '--summary-card-border-width': '2px', // Ensure 'px' unit is added here - used in calc() which requires units
      '--summary-card-border-color': m('--neutral-v500'),
      '--summary-card-hover-border-color': m('--neutral-v700'),
      '--summary-card-box-shadow': 'none',
      '--summary-card-hover-box-shadow': m('--box-shadow-m'),
    },
  },
  [MotifScheme.QUINARY]: {
    name: 'Snap Quinary',
    [MotifComponent.ROOT]: {
      '--font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      // H1
      '--h1-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h1-desktop-font-size': '104px',
      '--h1-desktop-font-size-compact': '88px',
      '--h1-desktop-font-line-height': '120%',
      '--h1-desktop-font-weight': '400',
      '--h1-desktop-font-stretch': 'normal',
      '--h1-desktop-font-text-transform': 'none',
      '--h1-mobile-font-size': '56px',
      '--h1-mobile-font-line-height': '120%',
      '--h1-mobile-font-weight': '400',
      '--h1-mobile-font-stretch': 'normal',
      '--h1-mobile-font-text-transform': 'none',
      // H2
      '--h2-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      '--h2-desktop-font-size': '64px',
      '--h2-desktop-font-line-height': '120%',
      '--h2-desktop-font-weight': '400',
      '--h2-desktop-font-stretch': 'normal',
      '--h2-desktop-font-text-transform': 'uppercase',
      '--h2-desktop-font-letter-spacing': '0',
      '--h2-mobile-font-size': '48px',
      '--h2-mobile-font-line-height': '120%',
      '--h2-mobile-font-weight': '400',
      '--h2-mobile-font-stretch': 'normal',
      '--h2-mobile-font-text-transform': 'uppercase',
      '--h2-mobile-font-letter-spacing': '0',
      // H3
      '--h3-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      // H4
      '--h4-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      // H5
      '--h5-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      // H6
      '--h6-font-family': `${FontFamily.GRAPHIK}, Helvetica, Tahoma, Arial, sans-serif`,
      // action colors
      '--action-text-hover-color': m('--neutral-v700'),
      '--action-indicator-hover-opacity': '0',
    },
    [MotifComponent.HEADER]: {
      '--global-header-bg-color': m('--neutral-v0'),
      '--global-header-border-color': m('--neutral-v700'),
      '--global-header-nav-screen-global-links-bg-color': m('--neutral-v0'),
      '--global-header-navigator-item-hover-color': m('--action-text-hover-color'),
      '--global-header-navigator-item-desktop-font-text-transform': 'uppercase',
    },
    [MotifComponent.FOOTER]: {
      '--footer-bg-color': m('--neutral-v0'),
      '--footer-bar-bg-color': m('--neutral-v0'),
    },
    [MotifComponent.CAROUSEL]: {
      '--carousel-active-dot-color': m('--neutral-v700'),
      '--carousel-card-border-radius': '0',
      '--carousel-card-box-shadow': 'none',
      '--carousel-card-hover-box-shadow': 'none',
      '--carousel-card-desktop-grid-gap': '32px',
      '--carousel-card-mobile-grid-gap': '16px',
      '--carousel-card-border-color': 'transparent',
      '--carousel-card-hover-border-color': 'transparent',
      '--carousel-card-bg-color': 'transparent',
      '--carousel-card-hover-bg-color': 'transparent',
      '--carousel-inactive-dot-color': 'rgba(133, 141, 148, 0.50)',
      '--carousel-card-desktop-text-align': 'center',
      '--carousel-card-desktop-text-padding': `${m('--spacing-m')} 0`,
      '--carousel-card-mobile-text-padding': `${m('--spacing-m')} 0`,
    },
    [MotifComponent.MEDIA]: {
      '--media-border-radius': '0px',
    },
  },
});
